"use client";

import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { isTooltipPlacement, type TooltipPlacement, type TooltipLinkTarget } from "./tooltip.shared";
import styles from "./tooltip.module.css";
import { withSafeRender } from "../safe-render";

/* Types only: a Server Component must import the values from ./tooltip.shared,
   never from this "use client" file. WAT-12029. */
export type { TooltipPlacement, TooltipLinkTarget };

/* Measures before paint on the client, so a flipped panel never shows on the
   wrong side for a frame. Falls back to useEffect on the server, where
   useLayoutEffect warns. */
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

export interface TooltipProps {
  /** The explanation shown on hover/focus/tap, as plain text. A sentence or two.
   *  Ignored when `children` is set. */
  content?: string;
  /**
   * Rich explanation: ready-made React nodes (bold, line breaks, links) that
   * the caller has already sanitized. Wins over `content` when both are set;
   * `linkLabel`/`linkHref` still add their link after it. Text builds this on
   * the server from a CMS `<tooltip>` tag, so Tooltip itself never parses HTML.
   * Pair it with `containsLink` when it holds a link.
   */
  children?: React.ReactNode;
  /**
   * Tells Tooltip `children` holds a followable link, so the pop-up is a
   * disclosure (aria-expanded, no role="tooltip") as the link fields make it.
   * Tooltip does not look inside `children`; the caller says. No effect
   * without `children`.
   */
  containsLink?: boolean;
  /** Accessible name of the icon. Override when a page has several tooltips. */
  label?: string;
  /** Preferred side (below by default, per the frame); flips when there is not enough room. */
  placement?: TooltipPlacement;
  /** Optional link wording. Needs `linkHref` too, or nothing renders. */
  linkLabel?: string;
  /** Optional link destination. Needs `linkLabel` too. */
  linkHref?: string;
  /** "_blank" opens a new tab (adds rel="noopener noreferrer" and announces it). */
  linkTarget?: TooltipLinkTarget;
  className?: string;
}

/* The classes the Text atom draws for body-3. Tooltip cannot import Text, which
   imports Tooltip, so the same global classes are written out here. The spec
   "wears the same classes the Text atom draws for body-3" is the drift guard —
   keep it while this copy exists. */
const PANEL_TEXT = "dds-text dds-body-3 dds-tone-primary";

/** Mirrors `--spacing-8` in CSS — same token drives the trigger-to-panel gap,
 *  the horizontal viewport inset, and the flip breathing room. ⚠ Nothing at
 *  runtime links the two; move this if the token moves. */
const SPACING_8 = 8;
const VIEWPORT_MARGIN = SPACING_8;
const TRIGGER_PANEL_GAP = SPACING_8;

/* Jitter floor, not a drawn value — a drag-resize walks the anchor a few px
 * per frame, and without a band the panel ping-pongs a full panel-height
 * whenever the room sits on `needed`. WAT-11932. */
const FLIP_HYSTERESIS = 24;

/* Snaps to the device-pixel grid, same reason as Popper's `roundOffsetsByDPR`:
 * a fractional offset antialiases the panel edge across two device pixels and
 * re-rounds it on every repaint. Reads the DPR per call — zoom and DevTools
 * device overrides both change it under a mounted component. WAT-11932. */
function snapToDevicePixels(px: number) {
  const dpr = (typeof window === "undefined" ? 1 : window.devicePixelRatio) || 1;
  return Math.round(px * dpr) / dpr;
}

/**
 * Tooltip — an info icon that reveals a short explanation. ARIA APG pattern:
 * <button> trigger, aria-describedby -> role="tooltip", Escape to dismiss —
 * or a disclosure (aria-expanded/aria-controls, no role) when a link is set,
 * or when `children` holds a link or a control (`containsLink`).
 * Client component: Escape and the viewport flip need runtime measurement.
 * Spec: FUSION ECLIPSE, "Dev specs" > "Tooltip" (10219:24940).
 */
function TooltipImpl({
  content = "",
  children,
  containsLink = false,
  label = "More information",
  placement = "bottom",
  linkLabel,
  linkHref,
  linkTarget = "_self",
  className
}: TooltipProps) {
  // Both or neither — half a link renders nothing.
  const hasLink = Boolean(linkLabel && linkHref);
  // CMS field: anything off TOOLTIP_PLACEMENTS falls back to the frame's bottom.
  const preferred: TooltipPlacement = isTooltipPlacement(placement) ? placement : "bottom";

  /* ⚠ The panel changes role when it holds a link or a control. The APG
     forbids interactive content in a role="tooltip", which flattens into the
     trigger's description and announces a link as bare words — so then the
     panel is a DISCLOSURE instead: aria-expanded/aria-controls, no role.
     The open panel is still the trigger's description, so a screen reader
     reads its words on focus rather than only "expanded"; the link stays a
     real link, reached with Tab. */
  /* Rich children win over `content`; an empty one counts as not set. */
  const hasRich = children !== undefined && children !== null && children !== false && children !== "";
  const isDisclosure = hasLink || (hasRich && containsLink === true);
  // CMS field: anything but the exact opt-in falls back to same-tab.
  const opensNewTab = linkTarget === "_blank";
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<TooltipPlacement>(preferred);
  const [shift, setShift] = useState(0);
  /* Viewport-space top/left for the panel — the panel is promoted into the
     top layer via `popover`, so it is positioned in viewport coordinates set
     from the trigger's getBoundingClientRect() rather than CSS transforms. */
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);

  const panelId = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  /* The live side, so the flip rule can read what is on screen without `side`
     becoming a dependency of the effect that sets it. */
  const sideRef = useRef<TooltipPlacement>(preferred);
  // Stops the Escape focus() re-entering onFocus and re-opening the panel.
  const restoringFocusRef = useRef(false);
  // Open state BEFORE the tap began — pointerdown fires before focus, so the
  // click toggle isn't fooled by the focus handler having opened it already.
  const openAtPointerDownRef = useRef<boolean | null>(null);
  /* How focus arrived: a click fires pointerdown just before focus, keyboard
     focus never does. ⚠ A keyboard-focused panel must survive a stray pointer
     (WCAG 1.4.13 persistent); a click-focused one still closes on hover-away. */
  const pointerSessionRef = useRef(false);
  const keyboardFocusRef = useRef(false);

  const close = useCallback(() => setOpen(false), []);

  /* Panel only, NOT the wrapper: a click focuses the trigger in most browsers,
     and a wrapper-wide guard would then stop pointer-leave from ever closing.
     Only a keyboard user standing on the link needs protecting. */
  const focusIsInsidePanel = () => {
    const panel = panelRef.current;
    const active = typeof document === "undefined" ? null : document.activeElement;
    return Boolean(panel && active && panel.contains(active));
  };

  // Escape, on the document — a wrapper handler misses it when the pointer is
  // over the panel with focus still on the trigger.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      /* ⚠ preventDefault + stopPropagation, CAPTURE phase: an enclosing
         <dialog> (e.g. CartSideSheet) fires its own Escape-dismiss `cancel`
         event on the same keydown — only preventDefault here stops it. */
      event.preventDefault();
      event.stopPropagation();
      // If focus is on the link, hand it back to the trigger before unmounting.
      const panel = panelRef.current;
      const active = document.activeElement;
      const restore = Boolean(panel && active && panel.contains(active));

      // Escape is an explicit dismissal — without this, a hover reopen could
      // not be closed by pointer-leave while the trigger still held keyboard
      // focus. The guard re-arms on the next keyboard focus event.
      keyboardFocusRef.current = false;
      close();

      if (restore) {
        restoringFocusRef.current = true;
        triggerRef.current?.focus();
        restoringFocusRef.current = false;
      }
    };
    // Capture phase: an ancestor widget calling stopPropagation in the
    // bubble must not be able to disarm dismissal (WCAG 1.4.13 dismissible).
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, close]);

  /* Promote the panel into the top layer via the native popover API. Runs
     before the measurement effect below (layout effects execute in
     declaration order on the same commit), so the panel has a real box
     when measured. try/catch covers jsdom and older browsers with no
     `showPopover` — the panel still renders, minus the top-layer escape. */
  useIsomorphicLayoutEffect(() => {
    if (!open) {
      return;
    }
    const panel = panelRef.current;
    /* The `in` is for the compiler, not the runtime: TypeScript 4.9's lib.dom
       predates the Popover API, so it is what lets the typeof below narrow a
       member HTMLElement is not declared to have. Drop it on TS >= 5.2. */
    if (!panel || !("showPopover" in panel) || typeof panel.showPopover !== "function") {
      return;
    }
    try {
      panel.showPopover();
    } catch {
      /* Already showing, or the DOM revoked support — nothing to do. */
    }
  }, [open]);

  /* ⚠ Measures the LAYOUT viewport, not the window: `innerWidth`/`100vw`
     count the scrollbar gutter, which the panel cannot use. WAT-12029. */
  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) {
      return;
    }

    const root = document.documentElement;
    const viewportHeight = root.clientHeight;
    const width = root.clientWidth;
    /* ⚠ Written to the DOM directly, before reading the box below — publishing
       only through React state left a one-render lag where the box was read
       under the OLD clamp. */
    panel.style.setProperty("--dds-tooltip-viewport", `${width}px`);

    const anchor = trigger.getBoundingClientRect();
    const box = panel.getBoundingClientRect();

    // Zero-sized box = nothing to measure (jsdom, display:none) — keep the preference.
    if (box.height === 0 || box.width === 0) {
      sideRef.current = preferred;
      setSide(preferred);
      setShift(0);
      return;
    }

    /* Beside the icon, vertically centred. Same two-threshold shape as the
       vertical axis (WAT-11932): flip away at `neededH`, return at `clearsH`. */
    if (preferred === "left" || preferred === "right") {
      const roomH = {
        left: anchor.left,
        right: width - anchor.right
      };
      const neededH = box.width + TRIGGER_PANEL_GAP + VIEWPORT_MARGIN;
      const clearsH = neededH + FLIP_HYSTERESIS;
      const fits = {
        left: roomH.left >= neededH,
        right: roomH.right >= neededH
      };

      /* sideRef.current is "top"/"bottom" only while in the fallback below.
         Leaving it is a RETURN to the authored side, so it needs `clearsH`
         too — see design-conflicts/tooltip.mdx. */
      const recoveringFromFallback = sideRef.current !== "left" && sideRef.current !== "right";
      const canLeaveFallback = !recoveringFromFallback || roomH[preferred] >= clearsH;

      if ((fits.left || fits.right) && canLeaveFallback) {
        const currentH: "left" | "right" =
          sideRef.current === "left" || sideRef.current === "right" ?
            sideRef.current :
            preferred;
        const otherH: "left" | "right" = currentH === "left" ? "right" : "left";
        let resolvedSide: TooltipPlacement = currentH;

        if (!fits[currentH] && fits[otherH]) {
          resolvedSide = otherH;
        } else if (currentH !== preferred && roomH[preferred] >= clearsH) {
          // Back to the authored side, once it comfortably fits again.
          resolvedSide = preferred;
        }

        sideRef.current = resolvedSide;
        setSide(resolvedSide);
        setShift(0);

        // Vertically centred on the trigger, clamped within the viewport —
        // the panel has no vertical preference of its own on this axis.
        const centreY = anchor.top + anchor.height / 2;
        const wantedTop = centreY - box.height / 2;
        const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - VIEWPORT_MARGIN - box.height);
        const top = snapToDevicePixels(
          Math.min(Math.max(wantedTop, VIEWPORT_MARGIN), maxTop)
        );
        const left = snapToDevicePixels(
          resolvedSide === "right" ?
            anchor.right + TRIGGER_PANEL_GAP :
            anchor.left - box.width - TRIGGER_PANEL_GAP
        );
        setPanelPos({
          top,
          left
        });
        return;
      }
      // Neither side has room (e.g. a 370px panel on a phone) — fall through
      // to the top/bottom logic below, anchored on the frame's own default
      // side rather than an unauthored vertical preference.
    }

    /* Vertical: flipping away from a side that no longer fits is immediate,
       no band — the band only guards the return to the authored side, so a
       marginal recovery does not flip back and forth on jitter. */
    const verticalPreferred: "top" | "bottom" = preferred === "top" ? "top" : "bottom";
    const room: Record<"top" | "bottom", number> = {
      top: anchor.top,
      bottom: viewportHeight - anchor.bottom
    };
    const needed = box.height + VIEWPORT_MARGIN * 2;
    const clears = needed + FLIP_HYSTERESIS;

    const currentV: "top" | "bottom" =
      sideRef.current === "top" || sideRef.current === "bottom" ?
        sideRef.current :
        verticalPreferred;
    const otherV: "top" | "bottom" = currentV === "top" ? "bottom" : "top";
    let resolvedSide: TooltipPlacement = currentV;

    if (room[currentV] < needed && room[otherV] >= needed) {
      resolvedSide = otherV;
    } else if (currentV !== verticalPreferred && room[verticalPreferred] >= clears) {
      // Back to the authored side, once it comfortably fits again.
      resolvedSide = verticalPreferred;
    }

    sideRef.current = resolvedSide;
    setSide(resolvedSide);

    /* Horizontal: the LEFT EDGE must land on the device-pixel grid, so every
       branch snaps the resolved absolute left — snapping only the nudge can
       still leave a fractional edge when the centred position already is one. */
    const centre = anchor.left + anchor.width / 2;
    const wantedLeft = centre - box.width / 2;
    const wantedRight = centre + box.width / 2;
    let resolvedLeft: number;
    if (wantedLeft < VIEWPORT_MARGIN) {
      resolvedLeft = VIEWPORT_MARGIN;
    } else if (wantedRight > width - VIEWPORT_MARGIN) {
      resolvedLeft = width - VIEWPORT_MARGIN - box.width;
    } else {
      resolvedLeft = wantedLeft;
    }
    resolvedLeft = snapToDevicePixels(resolvedLeft);
    setShift(resolvedLeft - wantedLeft);

    /* Viewport-space top/left — the panel is promoted into the top layer, so
       it is positioned against the viewport rather than the wrapper. Snapped
       same as `left`, or a fractional anchor still shimmers vertically. */
    const top = snapToDevicePixels(
      resolvedSide === "bottom" ?
        anchor.bottom + TRIGGER_PANEL_GAP :
        anchor.top - box.height - TRIGGER_PANEL_GAP
    );
    setPanelPos({
      top,
      left: resolvedLeft
    });
  }, [preferred]);

  // Flip and shift on open, before the browser paints.
  useIsomorphicLayoutEffect(() => {
    if (!open) {
      setShift(0);
      setSide(preferred);
      sideRef.current = preferred;
      setPanelPos(null);
      return;
    }
    measure();
    // content, children and the link fields change the panel's measured size.
  }, [open, preferred, content, children, linkLabel, linkHref, measure]);

  /* Re-measure while open when the viewport changes, so a resized or rotated
     window cannot leave the panel flipped wrong or shifted off-screen.
     Through a single-frame guard, the same as nav-shell: the two rect reads
     are forced layout and a drag-resize fires this continuously. WAT-11932. */
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let frame = 0;
    const onResize = () => {
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          measure();
        });
      }
    };
    /* Top-layer promotion means an ancestor scrolling the trigger away
       leaves the panel floating — close rather than re-track scroll on
       every ancestor. Capture phase so stopPropagation cannot swallow it. */
    const onScroll = () => close();
    /* `passive: true` tells the browser we will not call `preventDefault`,
       so scroll-optimization paths (composited scrolling) stay on. Without
       it, a `capture` listener on scroll opts the whole document out of
       those. */
    const scrollOpts = {
      capture: true,
      passive: true
    } as const;
    window.addEventListener("resize", onResize);
    document.addEventListener("scroll", onScroll, scrollOpts);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("scroll", onScroll, scrollOpts);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [open, close, measure]);

  /* Touch has no blur and no pointer-leave: Safari never focuses a <button> on
     tap, so a tap elsewhere must dismiss explicitly (WCAG 1.4.13 dismissible). */
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: PointerEvent) => {
      const wrapper = wrapperRef.current;
      if (wrapper && event.target instanceof Node && !wrapper.contains(event.target)) {
        close();
      }
    };
    // Capture phase, same reason as the Escape listener above.
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, close]);

  return (
    <span
      className={[styles.root, className].filter(Boolean).join(" ")}
      /* A stable hook for a parent's stylesheet: Text builds an in-copy icon,
         so the caller has no className to pass (In-Page Legal's word-space). */
      data-dds-tooltip=""
      /* Handlers live on the WRAPPER, not the trigger: pointer so moving onto
         the panel keeps it open (1.4.13), focus so Tab can reach the link. */
      onBlur={event => {
        // Focus moving within the component keeps it open.
        const next = event.relatedTarget as Node | null;
        if (next && wrapperRef.current?.contains(next)) {
          return;
        }
        keyboardFocusRef.current = false;
        close();
      }}
      onFocus={() => {
        if (restoringFocusRef.current) {
          return;
        }
        keyboardFocusRef.current = !pointerSessionRef.current;
        if (keyboardFocusRef.current) {
          // A press abandoned on a touch surface leaves no click behind to
          // consume the pre-tap state — a keyboard arrival makes it stale by
          // definition, and a stale value here eats the next Enter toggle.
          openAtPointerDownRef.current = null;
        }
        setOpen(true);
      }}
      onPointerEnter={event => {
        // Touch opens via the click toggle only — an unguarded enter here
        // makes every tap open-then-instantly-close.
        if (event.pointerType === "touch") {
          return;
        }
        setOpen(true);
      }}
      onPointerLeave={event => {
        if (event.pointerType === "touch") {
          return;
        }
        // Never yank the panel from a keyboard user — standing on the link,
        // or holding keyboard focus on the trigger while a stray pointer
        // passes through. A CLICK-focused trigger still closes on hover-away.
        if (focusIsInsidePanel() || keyboardFocusRef.current) {
          return;
        }
        close();
      }}
      ref={wrapperRef}
    >
      <button
        aria-controls={isDisclosure && open ? panelId : undefined}
        aria-describedby={open ? panelId : undefined}
        aria-expanded={isDisclosure ? open : undefined}
        aria-label={label}
        className={styles.trigger}
        onClick={() => {
          // Toggle against the pre-tap state; a keyboard "click" has no
          // pointerdown and falls through to a plain toggle.
          const wasOpen = openAtPointerDownRef.current;
          openAtPointerDownRef.current = null;
          if (wasOpen === null) {
            setOpen(current => !current);
            return;
          }
          setOpen(!wasOpen);
        }}
        onPointerCancel={() => {
          openAtPointerDownRef.current = null;
          pointerSessionRef.current = false;
        }}
        onPointerDown={() => {
          openAtPointerDownRef.current = open;
          pointerSessionRef.current = true;
        }}
        onPointerUp={() => {
          // After the click settles; focus (if any) has already fired.
          pointerSessionRef.current = false;
        }}
        /* Clears both sentinels after an abandoned press (down, then drag off),
           which would otherwise fool the next click toggle and read the next
           keyboard focus as a click. ⚠ Not on pointerup, and not for touch:
           click fires after both and still needs the pre-tap state. */
        onPointerLeave={event => {
          if (event.pointerType === "touch") {
            return;
          }
          openAtPointerDownRef.current = null;
          pointerSessionRef.current = false;
        }}
        ref={triggerRef}
        type="button"
      >
        {/* ⚠ 16x16, not the 16x15 the frame exports — that centred the glyph
            on a half pixel, antialiasing it across two rows and shimmering on
            drag-resize (WAT-11932). Do not "restore" the exported viewBox. */}
        <svg
          aria-hidden="true"
          className={styles.glyph}
          focusable="false"
          viewBox="0 -0.5 16 16"
        >
          <path
            d="M7.472 10.472V5.32H8.528V10.472H7.472ZM8 4.584C7.78667 4.584 7.62667 4.53067 7.52 4.424C7.41333 4.31733 7.36 4.16267 7.36 3.96C7.36 3.74667 7.41333 3.592 7.52 3.496C7.62667 3.38933 7.78667 3.336 8 3.336C8.17067 3.336 8.31467 3.38933 8.432 3.496C8.56 3.592 8.624 3.74667 8.624 3.96C8.624 4.17333 8.56 4.33333 8.432 4.44C8.31467 4.536 8.17067 4.584 8 4.584ZM8 13.88C7.09333 13.88 6.24 13.7093 5.44 13.368C4.64 13.0267 3.936 12.5573 3.328 11.96C2.73067 11.352 2.26133 10.648 1.92 9.848C1.57867 9.048 1.408 8.19467 1.408 7.288C1.408 6.37067 1.57867 5.51733 1.92 4.728C2.26133 3.928 2.73067 3.22933 3.328 2.632C3.936 2.024 4.64 1.54933 5.44 1.208C6.24 0.866666 7.09333 0.695999 8 0.695999C8.91733 0.695999 9.77067 0.866666 10.56 1.208C11.36 1.54933 12.0587 2.024 12.656 2.632C13.264 3.22933 13.7387 3.928 14.08 4.728C14.4213 5.51733 14.592 6.37067 14.592 7.288C14.592 8.19467 14.4213 9.048 14.08 9.848C13.7387 10.648 13.264 11.352 12.656 11.96C12.0587 12.5573 11.36 13.0267 10.56 13.368C9.77067 13.7093 8.91733 13.88 8 13.88ZM8 12.984C8.78933 12.984 9.52533 12.84 10.208 12.552C10.9013 12.2533 11.5093 11.8427 12.032 11.32C12.5547 10.7973 12.96 10.1947 13.248 9.512C13.5467 8.81867 13.696 8.07733 13.696 7.288C13.696 6.49867 13.5467 5.76267 13.248 5.08C12.96 4.38667 12.5547 3.77867 12.032 3.256C11.5093 2.73333 10.9013 2.328 10.208 2.04C9.52533 1.74133 8.78933 1.592 8 1.592C7.21067 1.592 6.46933 1.74133 5.776 2.04C5.09333 2.328 4.49067 2.73333 3.968 3.256C3.44533 3.77867 3.03467 4.38667 2.736 5.08C2.448 5.76267 2.304 6.49867 2.304 7.288C2.304 8.07733 2.448 8.81867 2.736 9.512C3.03467 10.1947 3.44533 10.7973 3.968 11.32C4.49067 11.8427 5.09333 12.2533 5.776 12.552C6.46933 12.84 7.21067 12.984 8 12.984Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {/* Panel stays INLINE (inside the DDS root) so its typography and
          glass tokens resolve — but carries `popover="manual"` so the
          component's own `showPopover()` promotes it into the browser's
          TOP LAYER. Top layer escapes: (a) any ancestor's overflow clip,
          (b) any ancestor's containing block from `transform` /
          `backdrop-filter`, (c) a modal <dialog>'s inert boundary. */}
      {open && (
        <span
          className={styles.panel}
          data-side={side}
          id={panelId}
          popover="manual"
          ref={panelRef}
          role={isDisclosure ? undefined : "tooltip"}
          style={
            {
              /* Off-screen until measured, then snapped into place — a first
                 paint before layout would otherwise flash at 0,0. */
              top: panelPos ? panelPos.top : -9999,
              left: panelPos ? panelPos.left : -9999,
              /* Override the popover UA defaults (`inset: 0; margin: auto`)
                 that would otherwise centre the panel in the viewport. */
              right: "auto",
              bottom: "auto",
              margin: 0,
              "--dds-tooltip-shift": `${shift}px`
              /* --dds-tooltip-viewport is set ONLY imperatively in measure()
                 — a second, state-backed copy here could race it stale. */
            } as React.CSSProperties
          }
        >
          <span
            className={hasRich ? `${PANEL_TEXT} dds-richText ${styles.rich}` : PANEL_TEXT}
          >
            {hasRich ? children : content}
            {hasLink && (
              <a
                className={styles.link}
                href={linkHref}
                /* noopener blocks reverse tabnabbing. */
                rel={opensNewTab ? "noopener noreferrer" : undefined}
                target={opensNewTab ? "_blank" : undefined}
              >
                {linkLabel}
                {/* Inside the <a> so it joins the accessible name. */}
                {opensNewTab && <span className="dds-sr-only"> (opens in a new tab)</span>}
              </a>
            )}
          </span>
        </span>
      )}
    </span>
  );
}
export const Tooltip = withSafeRender(TooltipImpl, "Tooltip");

export default Tooltip;
